import React, { useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import SavingsIcon from "@mui/icons-material/Savings";
import SellIcon from "@mui/icons-material/Sell";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";

import { getTickInterval } from "pages/admin/components/CopilotDashboard/chartUtils";
import { PRODUCT_COLORS, buildProductShare, buildStackedTrend, deloitteAmounts, hasDeloitteFields, productColorMap, formatMoney, describeQuantity, formatPercentChange, formatQuantity, formatShare, formatSignedMoney, formatTrendDate, getSpendInsights, metricLabel } from "./meteredFormat";
import { productLabel, skuLabel } from "./meteredLabels";
import ProductIcon from "./ProductIcon";

// Look and feel follows the Copilot dashboard: white 12px cards on the grey page, a title and subtitle with the
// date range as a small pill, green accent, the same table cell styling.
export const ACCENT = "#26890d";
// Deloitte greens (as used across the Copilot dashboard): the light one starts the chart / share bars.
export const DELOITTE_GREEN = "#86BC25";
export const DELOITTE_GREEN_DARK = "#43B02A";
// The Copilot dashboard's colour for what Deloitte actually pays after its negotiated discount.
export const DELOITTE_PURPLE = "#7B5EA7";

// The two extra columns on every table: the discount Deloitte's negotiated rate takes off the billed (net) amount, and
// what Deloitte pays after it. Coloured as on the Copilot dashboard.
export const DeloitteHeadCells = ({ percent }) => (
  <>
    <TableCell sx={{ ...headCellSx, color: ACCENT }} align="right">
      <Tooltip title={`Deloitte's negotiated ${percent != null ? `${percent}% ` : ""}discount, taken off the billed (net) amount.`} arrow>
        <span>Deloitte Discount</span>
      </Tooltip>
    </TableCell>
    <TableCell sx={{ ...headCellSx, color: DELOITTE_PURPLE }} align="right">
      <Tooltip title={`What Deloitte actually pays: the billed (net) amount less the ${percent != null ? `${percent}% ` : ""}discount.`} arrow>
        <span>Net after Deloitte Discount</span>
      </Tooltip>
    </TableCell>
  </>
);
// `amount` is a row or totals object from the API: it carries the Deloitte figures, or the net amount to work them out from.
export const DeloitteBodyCells = ({ amount, rate, sx }) => {
  const figures = deloitteAmounts(amount, rate);
  if (!figures) return null;
  return (
    <>
      <TableCell sx={{ ...sx, color: ACCENT, fontVariantNumeric: "tabular-nums" }} align="right" data-testid="deloitte-discount">{formatMoney(figures.discount)}</TableCell>
      <TableCell sx={{ ...sx, fontWeight: 600, color: DELOITTE_PURPLE, fontVariantNumeric: "tabular-nums" }} align="right" data-testid="deloitte-net">{formatMoney(figures.net)}</TableCell>
    </>
  );
};

export const headCellSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "#999",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  backgroundColor: "#fafafa",
  borderBottom: "2px solid #e8e8e8",
  py: 1.5,
};
export const bodyCellSx = { fontSize: "0.85rem", color: "#1a1a1a", py: 1.2, borderBottom: "1px solid #f0f0f0" };

export const DatePill = ({ from, to }) => (
  <Box
    sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.35, backgroundColor: "#f7f7f7", border: "1px solid #e8e8e8", borderRadius: "20px" }}
  >
    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#777" }}>
      {from} → {to}
    </Typography>
  </Box>
);

export const HeaderDatePill = ({ from, to }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.6,
      mt: 1,
      px: 1.2,
      py: 0.35,
      backgroundColor: "rgba(38,137,13,0.07)",
      border: "1px solid rgba(38,137,13,0.22)",
      borderRadius: "20px",
      width: "fit-content",
    }}
  >
    <CalendarTodayIcon sx={{ fontSize: "12px", color: ACCENT }} />
    <Typography sx={{ fontSize: "12px", color: ACCENT, fontWeight: 700, letterSpacing: "0.01em" }}>
      {from} &rarr; {to}
    </Typography>
  </Box>
);

export const ExpandButton = ({ label, onClick }) => (
  <Tooltip title={label} placement="left" arrow>
    <IconButton
      onClick={onClick}
      size="small"
      aria-label={label}
      sx={{ color: "#555", border: "1px solid #e8e8e8", borderRadius: "8px", "&:hover": { backgroundColor: "#f2f8e6", borderColor: ACCENT, color: ACCENT } }}
    >
      <OpenInFullIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

// Grey placeholders in the shape of what is about to load, so the card has about the right height from the start and
// nothing jumps when the data arrives. "table": a header and rows; "chart": bars; "list": icon, bar and amount rows.
const BAR_HEIGHTS = [38, 62, 48, 78, 55, 70, 88, 58, 44, 74, 84, 50];
export const CardSkeleton = ({ variant = "table", rows }) => {
  const count = rows ?? (variant === "list" ? 6 : 8);
  return (
    <Box role="status" aria-label="Loading" aria-busy="true" data-testid={`skeleton-${variant}`} sx={{ px: 3, py: 2.5, flex: 1, minHeight: variant === "chart" ? 320 : undefined }}>
      {variant === "chart" ? (
        <Box sx={{ height: 260, display: "flex", alignItems: "flex-end", gap: 1.5, px: 1 }}>
          {BAR_HEIGHTS.map((height, index) => (
            <Skeleton key={index} variant="rounded" animation="wave" sx={{ flex: 1, height: `${height}%`, transform: "none" }} />
          ))}
        </Box>
      ) : variant === "list" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
          {Array.from({ length: count }, (_, index) => (
            <Box key={index} sx={{ display: "grid", gridTemplateColumns: "32px minmax(60px, 1fr) 64px", alignItems: "center", gap: 1.5 }}>
              <Skeleton variant="circular" width={30} height={30} animation="wave" />
              <Skeleton variant="rounded" height={12} animation="wave" sx={{ width: `${95 - ((index * 13) % 45)}%` }} />
              <Skeleton variant="text" animation="wave" />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton variant="rounded" height={16} width="100%" animation="wave" sx={{ opacity: 0.6 }} />
          {Array.from({ length: count }, (_, index) => (
            <Box key={index} sx={{ display: "grid", gridTemplateColumns: "minmax(80px, 3fr) repeat(4, minmax(30px, 1fr))", gap: 2, alignItems: "center" }}>
              <Skeleton variant="rounded" height={14} animation="wave" sx={{ width: `${90 - ((index * 17) % 40)}%` }} />
              {[0, 1, 2, 3].map((cell) => (
                <Skeleton key={cell} variant="rounded" height={14} animation="wave" />
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// A white card with a title, subtitle and, on the right, the date pill and any actions. The first time its data loads
// the body is a skeleton shaped like the content (`skeleton`: "table" | "chart" | "list"), or a centred green spinner
// without one. When it reloads (a filter or date change) the old content stays under a translucent layer with a
// centred spinner, so the card never changes height. `headerMinHeight` and `hideDate` let two cards side by side keep
// their green rules level: the narrower one drops its date pill (the same range is on the card beside it) and both
// headers get the same minimum height.
export const Card = ({ title, subtitle, from, to, actions, loading = false, isInitialLoading = false, headerMinHeight, hideDate = false, skeleton, skeletonRows, children, sx }) => (
  <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e8e8e8", backgroundColor: "#fff", overflow: "hidden", position: "relative", display: "flex", flexDirection: "column", height: "100%", ...sx }}>
    <Box
      data-testid="card-header"
      // The Deloitte green rule under every card's header, above its table or chart.
      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", px: 3, py: 2, minHeight: headerMinHeight, boxSizing: "border-box", borderBottom: `3px solid ${DELOITTE_GREEN}` }}
    >
      <Box>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1a1a1a" }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.3 }}>{subtitle}</Typography>}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {from && to && !hideDate && <DatePill from={from} to={to} />}
        {actions}
      </Box>
    </Box>
    {isInitialLoading ? (
      skeleton ? (
        <CardSkeleton variant={skeleton} rows={skeletonRows} />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 240, flex: 1 }} role="status" aria-label="Loading">
          <CircularProgress size={32} sx={{ color: ACCENT }} />
        </Box>
      )
    ) : (
      // flex: 1 lets the body fill the card, so two cards side by side share one height.
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, position: "relative" }}>
        {children}
        {loading && (
          <Box
            role="status"
            aria-label="Loading"
            data-testid="card-reloading"
            sx={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "flex-start", pt: 9, backgroundColor: "rgba(255, 255, 255, 0.82)", zIndex: 2 }}
          >
            <CircularProgress size={32} sx={{ color: ACCENT }} />
          </Box>
        )}
      </Box>
    )}
  </Paper>
);

export const EmptyNote = ({ children }) => (
  <Typography sx={{ p: 4, textAlign: "center", color: "#999", fontSize: "0.85rem", fontStyle: "italic" }}>{children}</Typography>
);

// A change against the previous period as a small neutral chip: arrow, signed amount and percent. Deliberately not
// red / green — a rise isn't automatically bad and a fall isn't automatically a saving. A null percentage means the
// previous amount was zero ("no baseline"), which is different from 0%.
export const DeltaChip = ({ delta, percent, size = "small", hidePercent = false }) => {
  if (delta === null || delta === undefined) return null;
  const pct = formatPercentChange(percent);
  const Icon = delta > 0 ? TrendingUpIcon : delta < 0 ? TrendingDownIcon : TrendingFlatIcon;
  return (
    <Box
      data-testid="delta-chip"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.9, py: 0.2, borderRadius: "6px", backgroundColor: "#f1f1f1", color: "#333", width: "fit-content", fontSize: size === "small" ? "0.75rem" : "0.82rem", fontWeight: 600 }}
    >
      <Icon sx={{ fontSize: 15 }} />
      <span>{formatSignedMoney(delta)}</span>
      {!hidePercent && <span style={{ fontWeight: 400, color: "#666" }}>{pct ?? "no baseline"}</span>}
    </Box>
  );
};

// The top cards, in the same compact style as the Copilot dashboard's stat cards: an icon and label, the figure, and
// one muted line under it. Gross, discount and net are all shown — fully discounted usage has a net of $0 but is still
// real activity — and unattributed cost is called out, never folded silently into the total. The change against the
// previous period sits beside the figure; its dates are on the line below. `deloitte` ({ rate, net }) adds the Net Cost
// after Deloitte Discount tile: what Deloitte actually pays for everything in view once its negotiated discount is taken off.
export const KpiCards = ({ summary, isLoading = false, range, deloitte }) => {
  const total = summary?.total ?? {};
  const unattributed = summary?.unattributed ?? {};
  const comparison = summary?.comparison ?? null;
  const insight = range ? getSpendInsights(total.net_amount, range) : null;
  const netNote = insight ? `${formatMoney(insight.avgPerDay)}/day on average` : "what is actually billed";
  const cards = [
    {
      label: "Net cost",
      accent: "rgba(134, 188, 34, 1)",
      icon: <SellIcon sx={{ color: "rgba(134, 188, 34, 1)", fontSize: 24 }} />,
      value: formatMoney(total.net_amount),
      key: "net_amount",
      note: netNote,
      tooltip: "Total billed for the selected period and filters, after discounts. The note is the plain average per day.",
    },
    {
      label: "Gross usage",
      accent: "#F28C00",
      icon: <PaidOutlinedIcon sx={{ color: "#F28C00", fontSize: 24 }} />,
      value: formatMoney(total.gross_amount),
      key: "gross_amount",
      note: "before discounts",
      tooltip: "Total usage at list price, before any discounts.",
    },
    {
      label: "Discounted",
      accent: ACCENT,
      icon: <SavingsIcon sx={{ color: ACCENT, fontSize: 24 }} />,
      value: formatMoney(total.discount_amount),
      key: "discount_amount",
      note: `${formatShare(total.discount_amount ?? 0, total.gross_amount ?? 0)} of gross`,
      tooltip: "Usage that was discounted. A fully discounted amount has a net cost of $0 but is still real activity.",
    },
  ];
  if (deloitte) {
    const figures = deloitteAmounts(deloitte.amount, deloitte.rate) ?? { net: 0, discount: 0 };
    const percent = deloitte.percent;
    cards.push({
      label: "Net Cost after Deloitte Discount",
      testId: "kpi-deloitte",
      accent: DELOITTE_PURPLE,
      icon: <SellIcon sx={{ color: DELOITTE_PURPLE, fontSize: 24 }} />,
      value: formatMoney(figures.net),
      note: `${percent != null ? `${percent}% ` : ""}Deloitte discount · saves ${formatMoney(figures.discount)}`,
      tooltip: `What Deloitte actually pays for everything in view: the billed (net) cost less its negotiated ${percent != null ? `${percent}% ` : ""}discount, which applies to every product.`,
    });
  }
  cards.push({
    label: "Unattributed",
    accent: "#E53935",
    icon: <WarningAmberIcon sx={{ color: "#E53935", fontSize: 24 }} />,
    value: formatMoney(unattributed.net_amount),
    note: `no username · gross ${formatMoney(unattributed.gross_amount)}`,
    tooltip: "Usage that couldn't be tied to a person. It's included in the totals but not in the people ranking.",
  });
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: `repeat(${cards.length}, 1fr)` }, gap: 2 }}>
      {cards.map((card) => {
        const compare = comparison && card.key && !isLoading ? comparison : null;
        const prevRange = comparison ? `${formatTrendDate(comparison.from_date, "day")} – ${formatTrendDate(comparison.to_date, "day")}` : "";
        return (
          <Tooltip key={card.label} title={card.tooltip} placement="top" arrow componentsProps={{ tooltip: { sx: { fontSize: "0.8rem", lineHeight: 1.5, px: 1.5, py: 1 } } }}>
            <Paper
              elevation={0}
              data-testid={card.testId}
              sx={{
                borderRadius: "10px",
                border: `1px solid ${card.accent}`,
                borderTop: `3px solid ${card.accent}`,
                padding: "14px 18px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                backgroundColor: "#fff",
                cursor: "default",
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: "0 10px 24px rgba(0, 0, 0, 0.12)" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {card.icon}
                <Typography sx={{ fontSize: "0.8rem", color: "#666", fontWeight: 500, lineHeight: 1.25 }}>{card.label}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {isLoading ? (
                  <Skeleton variant="text" width="70%" sx={{ fontSize: "1.7rem" }} aria-label="Loading" />
                ) : (
                  <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.1 }}>{card.value}</Typography>
                )}
                {compare?.previous_has_records && (
                  <Box data-testid={`compare-${card.key}`} component="span" sx={{ display: "inline-flex" }}>
                    <DeltaChip delta={comparison.delta?.[card.key]} percent={comparison.percentage_change?.[card.key]} />
                  </Box>
                )}
              </Box>
              <Typography sx={{ fontSize: "0.7rem", color: "#999", lineHeight: 1.35 }} title={compare ? `Compared with ${comparison.from_date} → ${comparison.to_date}` : undefined}>
                {isLoading ? "\u00a0" : card.note}
                {compare?.previous_has_records && ` · vs ${prevRange} (${formatMoney(comparison.previous_totals?.[card.key])})`}
              </Typography>
              {compare && !compare.previous_has_records && (
                <Typography data-testid={`compare-${card.key}`} sx={{ fontSize: "0.68rem", color: "#999", lineHeight: 1.3 }}>
                  No records for {prevRange} to compare with — not proof of zero usage
                </Typography>
              )}
            </Paper>
          </Tooltip>
        );
      })}
    </Box>
  );
};

// How a grouped trend names and colours its series: a product by its readable name and product colour, a SKU by its
// readable name and unit.
const trendLabelOf = (groupBy) => (p) => (groupBy === "product" ? productLabel(p.product) : `${skuLabel(p.sku, p.product)}${p.unit_type ? ` (${p.unit_type})` : ""}`);
const trendColorOf = (groupBy, colors) => (series, index) => (groupBy === "product" ? colors?.[series.product] : null) ?? PRODUCT_COLORS[index % PRODUCT_COLORS.length];

// Hover card for the stacked trend: the groups with usage that day (biggest on top) and the day's total.
const StackedTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const entries = payload.filter((entry) => entry.value !== 0).reverse();
  const total = payload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
  return (
    <Box sx={{ backgroundColor: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", p: 1.2, fontSize: "0.78rem", boxShadow: "0 4px 14px rgba(0,0,0,0.1)" }}>
      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.5 }}>{label}</Typography>
      {entries.map((entry) => (
        <Box key={entry.dataKey} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: entry.color }} />
          <span style={{ flex: 1, color: "#555" }}>{entry.name}</span>
          <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{formatMoney(entry.value)}</span>
        </Box>
      ))}
      <Box sx={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #eee", mt: 0.6, pt: 0.6, fontWeight: 700 }}>
        <span>Total</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatMoney(total)}</span>
      </Box>
    </Box>
  );
};

// Cost over time as bars of the chosen measure, styled like the Copilot dashboard's charts. The API doesn't pad
// empty days, so the bars are sparse. With `groupBy` ("product" | "sku") the points carry a group and the bars are
// stacked, one colour per group; a group's missing days count as 0 so the stack is whole.
export const TrendChart = ({ points, granularity, metric, groupBy, colors }) => {
  if (!points?.length) return <EmptyNote>No usage in this period.</EmptyNote>;
  const grouped = groupBy === "product" || groupBy === "sku";
  const stacked = grouped ? buildStackedTrend(points, groupBy, metric, trendLabelOf(groupBy)) : null;
  const data = grouped
    ? stacked.rows.map((row) => ({ ...row, label: formatTrendDate(row.date, granularity) }))
    : points.map((point) => ({ ...point, label: formatTrendDate(point.date, granularity) }));
  const average = data.reduce((sum, point) => sum + (grouped ? point.total : point[metric] ?? 0), 0) / data.length;
  const colorOf = trendColorOf(groupBy, colors);
  return (
    // The chart fills whatever height its card has (a card beside a taller one stretches to match), with a minimum, so
    // there is no blank space under it. The absolutely positioned inner box gives the chart a height it can measure.
    <Box sx={{ flex: 1, display: "flex", minHeight: grouped ? 320 : 280, px: 2, py: 2, "& svg, & svg *": { outline: "none" } }} data-testid="metered-trend" data-grouped={grouped ? groupBy : undefined}>
      <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
      <Box sx={{ position: "absolute", inset: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
          <defs>
            <linearGradient id="meteredBarGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={DELOITTE_GREEN} stopOpacity={1} />
              <stop offset="100%" stopColor={DELOITTE_GREEN_DARK} stopOpacity={1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} />
          <YAxis
            tick={{ fontSize: 11, fill: "#999" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v}`}
            width={55}
          />
          {grouped ? (
            <RechartsTooltip content={<StackedTooltip />} cursor={{ fill: "#f7f7f7" }} />
          ) : (
            <RechartsTooltip formatter={(value) => [formatMoney(value), metricLabel(metric)]} labelStyle={{ fontWeight: 700 }} cursor={{ fill: "#f7f7f7" }} />
          )}
          <ReferenceLine y={average} stroke="#999" strokeDasharray="4 4" label={{ value: `avg ${formatMoney(average)}`, position: "insideTopRight", fontSize: 11, fill: "#777" }} />
          {grouped ? (
            <>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
              {stacked.series.map((series, index) => (
                <Bar key={series.id} dataKey={series.id} name={series.label} stackId="trend" fill={colorOf(series, index)} maxBarSize={32} />
              ))}
            </>
          ) : (
            <Bar dataKey={metric} fill="url(#meteredBarGradient)" radius={[4, 4, 0, 0]} maxBarSize={32} />
          )}
        </BarChart>
      </ResponsiveContainer>
      </Box>
      </Box>
    </Box>
  );
};

// The dashed vertical guide that follows the pointer, as on the Copilot dashboard's model usage chart.
const LINE_CURSOR = { stroke: "#cfcfcf", strokeWidth: 1, strokeDasharray: "4 4" };

// Cost over time as lines, one per product or SKU (or a single line for the total). With `cumulative` each line is a
// running total over the period. Same data as the trend above — a group's missing days count as 0.
export const TrendLines = ({ points, granularity, metric, groupBy, colors, cumulative = false }) => {
  if (!points?.length) return <EmptyNote>No usage in this period.</EmptyNote>;
  const grouped = groupBy === "product" || groupBy === "sku";
  let rows;
  let series;
  if (grouped) {
    ({ rows, series } = buildStackedTrend(points, groupBy, metric, trendLabelOf(groupBy)));
  } else {
    rows = [...points].sort((a, b) => String(a.date).localeCompare(String(b.date))).map((p) => ({ date: p.date, all: p[metric] ?? 0, total: p[metric] ?? 0 }));
    series = [{ id: "all", label: `${metricLabel(metric)} cost`, product: null }];
  }
  if (cumulative) {
    const running = {};
    rows = rows.map((row) => {
      const next = { ...row, total: 0 };
      series.forEach((s) => {
        running[s.id] = (running[s.id] ?? 0) + (row[s.id] ?? 0);
        next[s.id] = running[s.id];
        next.total += running[s.id];
      });
      return next;
    });
  }
  const data = rows.map((row) => ({ ...row, label: formatTrendDate(row.date, granularity) }));
  const colorOf = trendColorOf(groupBy, colors);
  return (
    <Box sx={{ height: 320, px: 2, py: 2, "& svg, & svg *": { outline: "none" } }} data-testid="metered-lines" data-grouped={grouped ? groupBy : undefined} data-cumulative={cumulative ? "true" : undefined}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} interval={getTickInterval(data.length)} />
          <YAxis
            tick={{ fontSize: 11, fill: "#999" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(Math.abs(v) >= 10000 ? 0 : 1)}k` : v}`}
            width={55}
          />
          {grouped ? (
            <RechartsTooltip content={<StackedTooltip />} cursor={LINE_CURSOR} />
          ) : (
            <RechartsTooltip formatter={(value) => [formatMoney(value), `${cumulative ? "Running " : ""}${metricLabel(metric)}`]} labelStyle={{ fontWeight: 700 }} cursor={LINE_CURSOR} />
          )}
          {grouped && <Legend wrapperStyle={{ fontSize: "0.75rem" }} formatter={(value) => <span style={{ color: "#555" }}>{value}</span>} />}
          {/* Plain smooth lines like the Copilot dashboard's model usage chart: no dot on every point, a dot only where the pointer is. */}
          {series.map((line, index) => (
            <Line key={line.id} type="monotone" dataKey={line.id} name={line.label} stroke={grouped ? colorOf(line, index) : ACCENT} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Products with their icon, amounts and share, expanding to SKUs. Sortable, with a totals row, a discount-rate column
// and a "Fully discounted" tag for rows that have real activity but a net cost of $0. Quantities only appear on SKU
// rows: units differ between SKUs (minutes, credits, GB…), so a product-level quantity would add things that can't
// be added, and the totals row leaves quantity out for the same reason.
const SORTERS = {
  name: (p) => productLabel(p.product).toLowerCase(),
  gross_amount: (p) => p.gross_amount ?? 0,
  discount_amount: (p) => p.discount_amount ?? 0,
  rate: (p) => ((p.gross_amount ?? 0) > 0 ? (p.discount_amount ?? 0) / p.gross_amount : 0),
  net_amount: (p) => p.net_amount ?? 0,
};

const isFullyDiscounted = (row) => (row.gross_amount ?? 0) > 0 && (row.net_amount ?? 0) === 0;

const FullyDiscountedTag = () => (
  <Tooltip title="All of this usage was discounted, so it costs $0 but is still real activity." arrow>
    <Chip size="small" label="Fully discounted" sx={{ fontSize: "0.66rem", height: 20, backgroundColor: "#eaf6e3", color: ACCENT, fontWeight: 600 }} />
  </Tooltip>
);

const ShareCell = ({ part, whole, color, sx }) => (
  <Box sx={{ minWidth: 110, ...sx }}>
    <Typography sx={{ fontSize: "0.8rem", textAlign: "right" }}>{formatShare(part, whole)}</Typography>
    <Box sx={{ height: 4, borderRadius: 4, backgroundColor: "#eee", mt: 0.4, overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: `${whole > 0 ? Math.min(Math.max((part / whole) * 100, 0.5), 100) : 0}%`, backgroundColor: color }} />
    </Box>
  </Box>
);

// A SKU row's quantity with its unit. A seat license's "user-months" is turned into seat-days where it can be, with the
// explanation in a tooltip.
const SkuQuantityCell = ({ sku, range, sx }) => {
  const { value, unit, hint } = describeQuantity(sku.quantity, sku.unit_type, range);
  const content = (
    <>
      {value}
      <Typography component="span" data-testid="sku-unit" sx={{ fontSize: "0.72rem", color: unit ? "#777" : "#bbb", ml: 0.6 }}>
        {unit ?? "unknown unit"}
      </Typography>
    </>
  );
  return (
    <TableCell sx={sx} align="right">
      {hint ? (
        <Tooltip title={hint} placement="top" arrow>
          <span data-testid="sku-quantity-hint" style={{ cursor: "help", borderBottom: "1px dotted #aaa" }}>{content}</span>
        </Tooltip>
      ) : (
        content
      )}
    </TableCell>
  );
};

export const ProductsTable = ({ products, selectedProduct, selectedSku, onFilterProduct, deloitteRate, discountPercent, range }) => {
  const [open, setOpen] = useState({});
  const [sort, setSort] = useState({ by: "net_amount", order: "desc" });
  const list = products ?? [];
  if (!list.length) return <EmptyNote>No products in this period.</EmptyNote>;

  const colors = productColorMap(list);
  // The Deloitte columns only appear when an AI Credits SKU is in the list, and only that SKU gets figures.
  const showDeloitte = deloitteRate !== undefined || list.every(hasDeloitteFields);
  const totals = list.reduce(
    (sum, p) => ({
      gross: sum.gross + (p.gross_amount ?? 0),
      discount: sum.discount + (p.discount_amount ?? 0),
      net: sum.net + (p.net_amount ?? 0),
      deloitteNet: sum.deloitteNet + (p.deloitte_net_amount ?? 0),
      deloitteDiscount: sum.deloitteDiscount + (p.deloitte_discount_amount ?? 0),
    }),
    { gross: 0, discount: 0, net: 0, deloitteNet: 0, deloitteDiscount: 0 }
  );
  // The totals row adds up the rows, so it carries the Deloitte figures only when every row has them.
  const totalsAmount = list.every(hasDeloitteFields)
    ? { net_amount: totals.net, deloitte_net_amount: totals.deloitteNet, deloitte_discount_amount: totals.deloitteDiscount }
    : { net_amount: totals.net };
  const rows = [...list].sort((a, b) => {
    const [x, y] = [SORTERS[sort.by](a), SORTERS[sort.by](b)];
    const result = typeof x === "string" ? x.localeCompare(y) : x - y;
    return sort.order === "asc" ? result : -result;
  });
  const allOpen = list.every((p) => open[p.product]);
  const toggleAll = () => setOpen(Object.fromEntries(list.map((p) => [p.product, !allOpen])));
  const toggleSort = (by) => setSort((s) => (s.by === by ? { by, order: s.order === "asc" ? "desc" : "asc" } : { by, order: by === "name" ? "asc" : "desc" }));
  const header = (label, by, align = "left") => (
    <TableCell sx={headCellSx} align={align} sortDirection={sort.by === by ? sort.order : false}>
      <TableSortLabel active={sort.by === by} direction={sort.by === by ? sort.order : "desc"} onClick={() => toggleSort(by)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 1 }}>
        <Button size="small" onClick={toggleAll} startIcon={allOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />} sx={{ textTransform: "none", fontSize: "0.78rem", color: "#555" }}>
          {allOpen ? "Collapse all" : "Expand all"}
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {header("Product / SKU", "name")}
              <TableCell sx={headCellSx} align="right">Quantity</TableCell>
              {header("Gross", "gross_amount", "right")}
              {header("Discount", "discount_amount", "right")}
              {header("Discount rate", "rate", "right")}
              {header("Net", "net_amount", "right")}
              {showDeloitte && <DeloitteHeadCells percent={discountPercent} />}
              <TableCell sx={headCellSx} align="right">Share of net</TableCell>
              {onFilterProduct && <TableCell sx={{ ...headCellSx, width: 48 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((product) => {
              const expanded = Boolean(open[product.product]);
              const skus = product.skus ?? [];
              const color = colors[product.product];
              const selected = selectedProduct === product.product;
              return (
                <React.Fragment key={product.product}>
                  <TableRow
                    hover
                    onClick={() => setOpen((o) => ({ ...o, [product.product]: !o[product.product] }))}
                    sx={{ "&:hover": { backgroundColor: "#f9fdf2" }, cursor: "pointer", backgroundColor: selected ? "#f2f8e6" : "inherit", boxShadow: selected ? `inset 3px 0 0 ${ACCENT}` : "none" }}
                    data-testid={`product-${product.product}`}
                    aria-selected={selected}
                  >
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <ProductIcon product={product.product} color={color} />
                        <Typography title={product.product} sx={{ fontWeight: 700, fontSize: "0.88rem" }}>{productLabel(product.product)}</Typography>
                        <Chip size="small" label={`${skus.length} SKU${skus.length === 1 ? "" : "s"}`} sx={{ fontSize: "0.68rem", height: 20 }} />
                        {isFullyDiscounted(product) && <FullyDiscountedTag />}
                        {selected && <Chip size="small" label="filtered" sx={{ fontSize: "0.68rem", height: 20, backgroundColor: "rgba(38,137,13,0.1)", color: ACCENT }} />}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: "#bbb" }} align="right">varies by SKU</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{formatMoney(product.gross_amount)}</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{formatMoney(product.discount_amount)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: "#555" }} align="right">{formatShare(product.discount_amount ?? 0, product.gross_amount ?? 0)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: ACCENT }} align="right">{formatMoney(product.net_amount)}</TableCell>
                    {showDeloitte && <DeloitteBodyCells amount={product} rate={deloitteRate} sx={bodyCellSx} />}
                    <TableCell sx={bodyCellSx} align="right">
                      <ShareCell part={product.net_amount ?? 0} whole={totals.net} color={color} />
                    </TableCell>
                    {onFilterProduct && (
                      <TableCell sx={bodyCellSx} align="center">
                        <Tooltip title={selected ? "Filtering the page by this product" : `Filter the page by ${productLabel(product.product)}`} arrow>
                          <IconButton
                            size="small"
                            aria-label={`Filter by ${productLabel(product.product)}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onFilterProduct(product.product);
                            }}
                            sx={{ color: selected ? ACCENT : "#888" }}
                          >
                            <FilterAltOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                  {expanded &&
                    skus.map((sku) => (
                      // The same SKU can appear once per unit, so a row is keyed by product / SKU / unit.
                      <TableRow key={`${product.product}|${sku.sku}|${sku.unit_type ?? ""}`} sx={{ backgroundColor: "#fcfcfc" }} data-testid={`sku-${sku.sku}`}>
                        <TableCell sx={{ ...bodyCellSx, pl: 9, color: "#555" }} title={sku.sku}>
                          {skuLabel(sku.sku, product.product)}
                          {selectedSku === sku.sku && selected && (
                            <Chip size="small" label="filtered" sx={{ ml: 1, fontSize: "0.68rem", height: 20, backgroundColor: "rgba(38,137,13,0.1)", color: ACCENT }} />
                          )}
                          {isFullyDiscounted(sku) && <Box component="span" sx={{ ml: 1 }}><FullyDiscountedTag /></Box>}
                        </TableCell>
                        <SkuQuantityCell sku={sku} range={range} sx={bodyCellSx} />
                        <TableCell sx={bodyCellSx} align="right">{formatMoney(sku.gross_amount)}</TableCell>
                        <TableCell sx={bodyCellSx} align="right">{formatMoney(sku.discount_amount)}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, color: "#555" }} align="right">{formatShare(sku.discount_amount ?? 0, sku.gross_amount ?? 0)}</TableCell>
                        <TableCell sx={bodyCellSx} align="right">{formatMoney(sku.net_amount)}</TableCell>
                        {showDeloitte && <DeloitteBodyCells amount={sku} rate={deloitteRate} sx={bodyCellSx} />}
                        <TableCell sx={bodyCellSx} align="right">
                          <ShareCell part={sku.net_amount ?? 0} whole={product.net_amount ?? 0} color={color} />
                          <Typography sx={{ fontSize: "0.66rem", color: "#aaa", textAlign: "right" }}>of {productLabel(product.product)}</Typography>
                        </TableCell>
                        {onFilterProduct && <TableCell sx={bodyCellSx} />}
                      </TableRow>
                    ))}
                </React.Fragment>
              );
            })}
            <TableRow data-testid="products-total" sx={{ backgroundColor: "#fafafa" }}>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 700, borderTop: "2px solid #e8e8e8" }}>Total</TableCell>
              <TableCell sx={{ ...bodyCellSx, color: "#bbb", borderTop: "2px solid #e8e8e8" }} align="right">—</TableCell>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 700, borderTop: "2px solid #e8e8e8" }} align="right">{formatMoney(totals.gross)}</TableCell>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 700, borderTop: "2px solid #e8e8e8" }} align="right">{formatMoney(totals.discount)}</TableCell>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: "#555", borderTop: "2px solid #e8e8e8" }} align="right">{formatShare(totals.discount, totals.gross)}</TableCell>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: ACCENT, borderTop: "2px solid #e8e8e8" }} align="right">{formatMoney(totals.net)}</TableCell>
              {showDeloitte && <DeloitteBodyCells amount={totalsAmount} rate={deloitteRate} sx={{ ...bodyCellSx, fontWeight: 700, borderTop: "2px solid #e8e8e8" }} />}
              <TableCell sx={{ ...bodyCellSx, borderTop: "2px solid #e8e8e8" }} align="right">{totals.net > 0 ? "100%" : "—"}</TableCell>
              {onFilterProduct && <TableCell sx={{ ...bodyCellSx, borderTop: "2px solid #e8e8e8" }} />}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// Products ranked as labelled horizontal bars — easier to compare precisely than a donut. Click a row to filter the
// page by that product. Shares are of the sum of every product in the list for the chosen measure (zero-safe).
export const ProductBars = ({ products, metric, selectedProduct, onSelect }) => {
  const list = (products ?? []).filter((p) => (p[metric] ?? 0) !== 0);
  const colors = productColorMap(products);
  const rows = [...list].sort((a, b) => (b[metric] ?? 0) - (a[metric] ?? 0));
  const total = rows.reduce((sum, p) => sum + (p[metric] ?? 0), 0);
  const top = Math.max(...rows.map((p) => Math.abs(p[metric] ?? 0)), 0);
  if (!rows.length) return <EmptyNote>No usage in this period.</EmptyNote>;
  return (
    // Only products with usage are listed, so the list can be short. Rows share the card's height (up to a limit) and the
    // block is centred, so a card beside a taller one doesn't end in a blank strip.
    <Box sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", justifyContent: "center", gap: 1.2, flex: 1 }} data-testid="metered-bars">
      {rows.map((p) => {
        const value = p[metric] ?? 0;
        const selected = selectedProduct === p.product;
        return (
          <Box
            key={p.product}
            data-testid={`bar-${p.product}`}
            onClick={onSelect ? () => onSelect(selected ? null : p.product) : undefined}
            sx={{ display: "grid", gridTemplateColumns: "minmax(110px, 170px) 1fr 92px 44px", alignItems: "center", gap: 1.2, px: 1, py: 0.6, flex: "1 1 0", minHeight: 44, maxHeight: 72, borderRadius: "8px", cursor: onSelect ? "pointer" : "default", backgroundColor: selected ? "#f2f8e6" : "transparent", "&:hover": onSelect ? { backgroundColor: "#f9fdf2" } : {} }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <ProductIcon product={p.product} color={colors[p.product]} />
              <Typography noWrap title={p.product} sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{productLabel(p.product)}</Typography>
            </Box>
            <Box sx={{ height: 12, borderRadius: 6, backgroundColor: "#eee", overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: `${top > 0 ? Math.max((Math.abs(value) / top) * 100, 1) : 0}%`, backgroundColor: value < 0 ? "#999" : colors[p.product] }} />
            </Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatMoney(value)}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#999", textAlign: "right" }}>{formatShare(value, total)}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// Share of cost by product as a donut, with a legend listing each product's amount and percentage. Clicking a
// slice (or a legend row) picks that product as the filter, the same as the chips. "Other" isn't clickable.
export const ProductShareDonut = ({ products, metric, selectedProduct, onSelect }) => {
  const slices = buildProductShare(products, metric, productLabel);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  if (!slices.length || total <= 0) return <EmptyNote>No usage in this period.</EmptyNote>;
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap", px: 3, py: 2.5, flex: 1 }} data-testid="metered-share">
      <Box sx={{ width: 190, height: 190, flexShrink: 0, "& svg, & svg *": { outline: "none" } }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={1} stroke="none" onClick={(slice) => slice?.id && onSelect?.(slice.id)}>
              {slices.map((slice) => (
                <Cell
                  key={slice.name}
                  fill={slice.color}
                  fillOpacity={selectedProduct && slice.id !== selectedProduct ? 0.35 : 1}
                  cursor={slice.id && onSelect ? "pointer" : "default"}
                />
              ))}
            </Pie>
            <RechartsTooltip formatter={(value, name) => [`${formatMoney(value)} · ${formatShare(value, total)}`, name]} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 0.4 }}>
        {slices.map((slice) => {
          const clickable = Boolean(slice.id && onSelect);
          return (
            <Box
              key={slice.name}
              onClick={clickable ? () => onSelect(slice.id) : undefined}
              data-testid={`share-${slice.id ?? "other"}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: "6px",
                cursor: clickable ? "pointer" : "default",
                backgroundColor: selectedProduct && slice.id === selectedProduct ? "#f2f8e6" : "transparent",
                "&:hover": clickable ? { backgroundColor: "#f9fdf2" } : {},
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: slice.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.82rem", color: "#555", flex: 1 }}>{slice.name}</Typography>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1a1a1a" }}>{formatMoney(slice.value)}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#999", minWidth: 36, textAlign: "right" }}>{formatShare(slice.value, total)}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
